import type { RecordResult } from '../@types/records';
import type { SyncDetails } from '../redux/middleware/sync-transforms';
import { startCleanupWorker, stopCleanupWorker } from './db-cleanup-manager';
import { createDBClient } from './db-client';
import type { DBClient } from './db-types';
import type {
  CleanupTask,
  InitDBConfig,
  OdinDBApiQueryEntryMetadata,
  OdinDBRecordMutationMetadata,
  OdinDBRecordMutationState,
  OdinDBSchema,
  UpdateRecordStateOptions,
} from './odin-db-types';
import { CleanupStrategies, OdinDBStores } from './odin-db-types';
import { buildKey, isOfflineEnabled, stableHash } from './odin-db-util';

const defaultTtl = 1 * 24 * 60 * 60 * 1000; // 1 day

// Configuration for the cleanup worker: which stores and indexes to use for cleanup.
const cleanupConfig: CleanupTask[] = [
  { storeName: OdinDBStores.ApiQueries, indexName: 'by-expiresAt', strategy: CleanupStrategies.DeleteExpired },
  {
    storeName: OdinDBStores.RecordMutations,
    indexName: 'by-state',
    strategy: CleanupStrategies.DeleteByExactMatch,
    matchValue: 'synced',
  },
];

let client: DBClient<OdinDBSchema> | null = null;
let currentUser: string | null = null;
let currentSchema: string | null = null;

/**
 * Initializes (or re-initializes) the Odin database client for a specific user and schema.
 *
 * If a client is already active for the same user and schema, that instance is reused.
 * Otherwise, any existing client is closed and a new one is created with the base (version 1) migration.
 *
 * @param config - Configuration object containing the user identifier and logical schema name.
 * @param config.userId - The preferred username.
 * @param config.schema - The current schema.
 *
 * @returns A ready-to-use database client scoped to the provided user and schema.
 *
 * @remarks
 * - The database name is derived as: `db-{schema}-{userId}`.
 * - Version is currently fixed at 1 with an initial (placeholder) migration.
 * - Calling this with different (userId, schema) pairs forces the previous client to close.
 *
 * @example
 * ```ts
 * const db = initDB({ userId: 'u123', schema: 'projects' });
 * // Reusing with same user + schema returns the cached client:
 * const same = initDB({ userId: 'u123', schema: 'projects' });
 * ```
 */
export function initDB(config: InitDBConfig) {
  const { userId, schema, onCleanupComplete } = config;

  if (client && currentUser === userId && currentSchema === schema) {
    return client;
  }

  // stop cleanup worker if running
  stopCleanupWorker();
  // close existing connection if any
  client?.close();
  client = null;

  currentUser = userId;
  currentSchema = schema;
  const dbName = `db-v1-${schema}-${userId}`;
  const dbVersion = 1;

  client = createDBClient<OdinDBSchema>({
    name: dbName,
    version: dbVersion,
    migrations: {
      1: async (db) => {
        if (!db.objectStoreNames.contains(OdinDBStores.ApiQueries)) {
          const store = db.createObjectStore(OdinDBStores.ApiQueries, { keyPath: 'key' });
          store.createIndex('by-endpoint', 'metadata.endpoint');
          store.createIndex('by-expiresAt', 'metadata.expiresAt');
        }
        if (!db.objectStoreNames.contains(OdinDBStores.RecordMutations)) {
          const store = db.createObjectStore(OdinDBStores.RecordMutations, { keyPath: 'id' });
          store.createIndex('by-formId', 'record.formId', { unique: false });
          store.createIndex('by-state', 'metadata.state', { unique: false });
          store.createIndex('by-operation', 'metadata.operation', { unique: false });
        }
      },
    },
  }) as DBClient<OdinDBSchema>;

  // start cleanup worker after the db has been initialized
  client.open().then(() => {
    console.log('[Main Thread] Database initialized, starting cleanup worker.');
    startCleanupWorker({
      dbName,
      dbVersion,
      tasks: cleanupConfig as CleanupTask[],
      onCleanupComplete,
    });
  });

  return client;
}

/**
 * Retrieves the initialized Odin database client instance.
 *
 * Ensures the underlying DB client has been properly initialized
 * before use. If the client is not yet available, an error is thrown.
 *
 * @returns The active DBClient instance parameterized with OdinDBSchema.
 * @throws Error If the Odin DB client has not been initialized.
 *
 * @example
 * const db = getDB();
 */
function getDB(): DBClient<OdinDBSchema> {
  if (!client) throw new Error('Odin DB not initialized');
  return client;
}

/**
 * Gracefully stops the background cleanup worker and closes the shared database connection.
 *
 * @remarks Any internal errors are caught and logged; the promise always resolves.
 *
 * @returns Promise that resolves once the shutdown sequence has completed.
 */
export async function closeDB(): Promise<void> {
  if (!isOfflineEnabled) return;

  try {
    stopCleanupWorker();

    const db = await getDB();
    db.close();
  } catch (error) {
    console.error('Error closing the database:', error);
  }
}

/**
 * Retrieves previously cached offline data for a specific RTK Query endpoint invocation.
 *
 * Constructs a deterministic cache key by combining the endpoint name with a stable hash
 * of the provided argument payload, then looks up the stored result in the IndexedDB (or
 * similar) "queries" store.
 *
 * @param endpointName - The unique RTK Query endpoint name used when the data was cached.
 * @param args - The argument(s) originally passed to the endpoint; hashed to form part of the key.
 *
 * @returns A promise resolving to the cached query result if present, or undefined if no entry exists.
 *
 * @throws If the underlying database cannot be opened or read.
 */
export async function getOfflineQueryData(endpointName: string, args: unknown) {
  const db = await getDB();
  const key = buildKey(endpointName, args);
  return db.get(OdinDBStores.ApiQueries, key);
}

/**
 * Persists API response data for offline use, keyed by the endpoint name and a stable hash of its arguments.
 *
 * @param endpointName - Logical name of the API endpoint whose data is being cached.
 * @param args - Original argument object used to invoke the endpoint; hashed to build a unique key.
 * @param data - The response payload to cache for offline retrieval.
 * @param ttl - Optional time-to-live in milliseconds; defaults to 1 day. Determines when the entry expires.
 * @returns A promise that resolves when the record has been written to the underlying 'queries' store.
 *
 * @remarks Overwrites any existing cached entry for the same endpoint/argument hash and timestamps the update.
 */
export async function setOfflineQueryData(
  endpointName: string,
  args: unknown,
  data: unknown,
  ttl: number = defaultTtl
) {
  const db = await getDB();
  const key = buildKey(endpointName, args);
  const metadata: OdinDBApiQueryEntryMetadata = {
    endpoint: endpointName,
    argsHash: stableHash(args),
    updatedAt: Date.now(),
    expiresAt: Date.now() + ttl,
  };
  const entry = {
    key,
    data,
    metadata,
  };
  return db.put(OdinDBStores.ApiQueries, entry);
}

/**
 * Queues a create record mutation in the local IndexedDB store with a pending state.
 *
 * @param id Unique identifier to assign to the stored mutation.
 * @param record The record data (without its id) to be created.
 *
 * @returns Promise that resolves once the mutation entry is persisted.
 */
export async function addRecord(id: number, record: RecordResult) {
  const db = await getDB();
  return db.put(OdinDBStores.RecordMutations, {
    id,
    record,
    metadata: {
      createdAt: Date.now(),
      state: 'pending',
      operation: 'create',
    },
  });
}

/**
 * Retrieves a specific record mutation by its ID from the local database.
 *
 * @param id - The unique identifier of the record to retrieve.
 * @returns A promise that resolves to the requested record, or undefined if not found.
 */
export async function getRecord(id: number) {
  const db = await getDB();
  return db.get(OdinDBStores.RecordMutations, id);
}

/**
 * Queues an update record mutation in the local IndexedDB store with a pending state.
 *
 * @param id Unique identifier to assign to the stored mutation.
 * @param record The changes to be applied to the record (the patch).
 * @param baseRecord The record state when the mutation was created.
 * @param metadata Optional existing mutation metadata to merge with; defaults to a new update operation.
 * @param syncDetails Optional additional details related to synchronization.
 *
 * @returns Promise that resolves once the mutation entry is persisted.
 */
export async function updateRecord(
  id: number,
  record: RecordResult,
  baseRecord?: RecordResult,
  metadata?: OdinDBRecordMutationMetadata,
  syncDetails?: SyncDetails
) {
  const db = await getDB();
  return db.put(OdinDBStores.RecordMutations, {
    id,
    record,
    baseRecord,
    metadata: {
      ...(metadata ?? { operation: 'update' }),
      updatedAt: Date.now(),
      state: 'pending',
    },
    syncDetails,
  });
}

/**
 * Deletes a record from the `RecordMutations` store by its ID.
 *
 * @param id - The unique identifier of the record to delete.
 * @returns A promise that resolves when the deletion is complete.
 */
export async function deleteRecord(id: number) {
  const db = await getDB();
  return db.delete(OdinDBStores.RecordMutations, id);
}

/**
 * Updates the state of a specific record mutation in the database.
 *
 * Supports optimistic locking by verifying the current state matches the expected state
 * before applying the update. This prevents race conditions when multiple processes
 * attempt to update the same record.
 *
 * @param id - The unique identifier of the record mutation to update.
 * @param newStateOrOptions - Either a simple new state string, or an options object for advanced control.
 * @param error - An optional error message string if the state change is due to a failure (only when newStateOrOptions is a string).
 * @returns A promise that resolves to true if update succeeded, false if optimistic lock failed.
 *
 * @throws Error if the record no longer exists in the database.
 */
export async function updateRecordState(
  id: number,
  newStateOrOptions: OdinDBRecordMutationState | UpdateRecordStateOptions,
  error?: string
): Promise<boolean> {
  const db = await getDB();

  // Normalize arguments to options object
  const options: UpdateRecordStateOptions =
    typeof newStateOrOptions === 'string' ? { newState: newStateOrOptions, error } : newStateOrOptions;

  const { newState, expectedState, error: errorMsg, incrementRetryCount } = options;

  // transaction to ensure the read-check-write operation is atomic
  return db.tx(OdinDBStores.RecordMutations, 'readwrite', async (tx) => {
    const store = tx.objectStore(OdinDBStores.RecordMutations);

    // Re-fetch the current record to check its state (optimistic locking)
    const currentRecord = await store.get(id);

    if (!currentRecord) {
      throw new Error(`Record mutation with id ${id} no longer exists`);
    }

    // If expectedState is provided, verify it matches
    if (expectedState !== undefined && currentRecord.metadata.state !== expectedState) {
      console.warn(
        `Optimistic lock failed for record ${id}: expected state '${expectedState}', but found '${currentRecord.metadata.state}'`
      );

      return false;
    }

    const newRetryCount = incrementRetryCount
      ? (currentRecord.metadata.retryCount ?? 0) + 1
      : currentRecord.metadata.retryCount;

    await store.put!({
      ...currentRecord,
      metadata: {
        ...currentRecord.metadata,
        state: newState,
        error: errorMsg,
        retryCount: newRetryCount,
      },
    });

    return true;
  });
}

/**
 * Retrieves all record mutation entries associated with a given form ID
 * using the 'by-formId' index in a readonly transaction.
 *
 * @param formId - The numeric identifier of the form whose records to fetch.
 * @returns Promise resolving to an array of matching record mutation objects.
 */
export async function getRecordsByFormId(formId: number) {
  const db = await getDB();
  return db.tx(OdinDBStores.RecordMutations, 'readonly', (tx) => {
    const store = tx.objectStore(OdinDBStores.RecordMutations);
    const index = store.index('by-formId');
    return index.getAll(formId);
  });
}

/**
 * Retrieves all record mutations from the database that match a specific synchronization state.
 *
 * This function opens a readonly transaction on the `RecordMutations` object store and uses
 * the 'by-state' index to filter records.
 *
 * @param state - The synchronization state to filter by ('pending', 'synced', or 'error').
 * @returns A promise that resolves to an array of record mutations matching the provided state.
 */
export async function getRecordsByState(state: OdinDBRecordMutationState) {
  const db = await getDB();
  return db.tx(OdinDBStores.RecordMutations, 'readonly', (tx) => {
    const store = tx.objectStore(OdinDBStores.RecordMutations);
    const index = store.index('by-state');
    return index.getAll(state);
  });
}
